import React, { useCallback, useMemo } from 'react';
import { Grid, Button, Typography, makeStyles } from '@material-ui/core';
import { useIntl, FormattedMessage } from 'react-intl';
import { v4 as uuid } from 'uuid';
import PropTypes from 'prop-types';
import { AddResourceIcon, Delta, NALabel } from '~/modules/common/components';
import FormattedHoursAndCost from '~/modules/resourcing/common/components/FormattedHoursAndCost';

const addResourceIcon = <AddResourceIcon />;

const diffHoursTooltip = (
  <FormattedMessage id="taskResourceAssignments.diffHoursToolTip" />
);

const diffCostsTooltip = (
  <FormattedMessage id="taskResourceAssignments.diffCostsToolTip" />
);

const NoValueComponet = (
  <NALabel
    tooltipTitle={
      <FormattedMessage id="taskResourceAssignments.someCostEstimatesUnavailableTooltip" />
    }
  />
);

const useStyles = makeStyles(theme => ({
  item: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  summary: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(0.5),
    marginTop: theme.spacing(1)
  },
  summaryLabels: {
    marginTop: theme.spacing(1),
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1)
  },
  summaryLabelsWithFF: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1)
  },
  costDiff: {
    paddingTop: theme.spacing(1)
  },
  costDiffWithoutDelta: {
    paddingTop: theme.spacing(3.25)
  },
  totalContainer: {
    textAlign: 'right'
  },
  totalContainerWithCost: {
    textAlign: 'right',
    alignSelf: 'center'
  }
}));

export const useDeltaStyles = makeStyles(theme => ({
  dateWithDeltaContainer: {
    ...theme.typography.caption,
    padding: theme.spacing(1 / 8, 1 / 4),
    borderRadius: theme.shape.borderRadius,
    display: 'inline-block',
    justifyContent: 'left'
  },
  deltaLabel: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: theme.spacing(1.5),
    fontWeight: theme.typography.fontWeightRegular
  },
  negative: {
    color: theme.palette.success.dark,
    backgroundColor: theme.palette.success.light
  },
  positive: {
    color: theme.palette.error.dark,
    backgroundColor: theme.palette.error.light
  }
}));

const useSummaryStyles = makeStyles(theme => ({
  currency: {
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
    paddingTop: theme.spacing(1)
  },
  hours: {
    ...theme.typography.body2,
    fontWeight: theme.typography.fontWeightBold
  }
}));

export const TaskResourceEstimatesFooter = ({
  taskEstimatedCost,
  taskEstimatedHours,
  onResourceEstimateAdd,
  showSummary,
  totalEstimatedHours,
  totalEstimatedCost,
  styleProps: { gridItemStyle },
  canViewCost,
  projectCurrency,
  initialAllocateResourceTimeValue,
  addResourceAssignmentButtonRef,
  isMissingCostRateForAtleastOneResource
}) => {
  const classes = useStyles();
  const summaryClasses = useSummaryStyles();
  const deltaClasses = useDeltaStyles();
  const { formatMessage } = useIntl();
  const diffHours = totalEstimatedHours - taskEstimatedHours;
  const diffCosts = totalEstimatedCost?.amount - taskEstimatedCost?.amount;

  const showTotalCostRow =
    canViewCost && taskEstimatedCost?.currency?.id === projectCurrency?.id;

  const formattedCosts = useMemo(() => {
    if (!isMissingCostRateForAtleastOneResource && totalEstimatedCost)
      return [totalEstimatedCost];

    return [];
  }, [isMissingCostRateForAtleastOneResource, totalEstimatedCost]);

  const addResourceAssignment = useCallback(() => {
    onResourceEstimateAdd({
      rowId: uuid(),
      isCreateAllocationEnabled: initialAllocateResourceTimeValue
    });
  }, [initialAllocateResourceTimeValue, onResourceEstimateAdd]);

  return (
    <Grid container>
      <Grid item className={classes.item} style={gridItemStyle}>
        <Button
          color="primary"
          startIcon={addResourceIcon}
          onClick={addResourceAssignment}
          data-qe-id="AddResourceAssignmentButton"
          ref={addResourceAssignmentButtonRef}
        >
          {formatMessage({ id: 'taskResourceAssignments.resource' })}
        </Button>
        {showSummary && (
          <Typography variant="body2" className={classes.summaryLabelsWithFF}>
            {formatMessage({ id: 'taskResourceAssignments.total' })}
          </Typography>
        )}
      </Grid>
      {showSummary && (
        <>
          <Grid item style={gridItemStyle}>
            <div className={classes.summary}>
              <div className={classes.diff}>
                {Boolean(diffHours) && (
                  <Delta
                    delta={diffHours}
                    precision={2}
                    suffix={formatMessage({
                      id: 'taskResourceAssignments.hoursSuffix'
                    })}
                    tooltipTitle={diffHoursTooltip}
                    classes={deltaClasses}
                  />
                )}
                {showTotalCostRow &&
                  !isMissingCostRateForAtleastOneResource && (
                    <div
                      className={
                        diffHours
                          ? classes.costDiff
                          : classes.costDiffWithoutDelta
                      }
                    >
                      {Boolean(diffCosts) && (
                        <Delta
                          delta={diffCosts}
                          prefix={`${totalEstimatedCost?.currencySymbol} `}
                          precision={2}
                          classes={deltaClasses}
                          tooltipTitle={diffCostsTooltip}
                        />
                      )}
                    </div>
                  )}
              </div>
              <FormattedHoursAndCost
                classes={summaryClasses}
                hours={totalEstimatedHours}
                hideCosts={!canViewCost}
                hoursSuffix={formatMessage({
                  id: 'taskResourceAssignments.hoursSuffix'
                })}
                costs={formattedCosts}
                showAbbreviatedValue={false}
                noValueComponent={
                  isMissingCostRateForAtleastOneResource
                    ? NoValueComponet
                    : undefined
                }
              />
            </div>
          </Grid>
          <Grid item></Grid>
        </>
      )}
    </Grid>
  );
};

TaskResourceEstimatesFooter.propTypes = {
  taskEstimatedCost: PropTypes.object,
  taskEstimatedHours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onResourceEstimateAdd: PropTypes.func,
  showSummary: PropTypes.bool,
  totalEstimatedHours: PropTypes.number,
  totalEstimatedCost: PropTypes.object,
  styleProps: PropTypes.object,
  canViewCost: PropTypes.bool,
  projectCurrency: PropTypes.object,
  initialAllocateResourceTimeValue: PropTypes.bool,
  addResourceAssignmentButtonRef: PropTypes.object,
  isMissingCostRateForAtleastOneResource: PropTypes.bool
};

export default TaskResourceEstimatesFooter;
